/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import { getAutocompleteEntitiesRoute } from '../autocomplete_entities'

import { createJsonResponse, makeHttpRequest } from './utils'
import { getElasticsearchVersion } from './version-utils'

import type { SettingsToRetrieve } from '../autocomplete_entities/types'
import type { StackDeployment, HttpResponse, RouteMapper } from './types'

// Returns the es config information
const handleEsConfig = async (deployment: StackDeployment): Promise<HttpResponse> => {
  const esResource = deployment?.resources.elasticsearch[0] ?? null
  const host = esResource?.info.metadata.endpoint ?? null

  // Make sure the host has https:// protocol if not already specified
  if (host && !host.startsWith('http://') && !host.startsWith('https://')) {
    const response = createJsonResponse({ host: `https://${host}` })
    const body = await response.json()
    return {
      body,
      response,
    }
  }

  // If no valid host, return empty response
  const response = createJsonResponse({})
  const body = await response.json()

  return {
    body,
    response,
  }
}

// Returns autocomplete entities for the console
const handleAutocompleteEntities = async <T extends { query?: unknown } = Record<string, unknown>>(
  deployment: StackDeployment,
  options?: T,
): Promise<unknown> => {
  if (!deployment) {
    throw new Error('Deployment not found for autocomplete entities')
  }

  const result = await getAutocompleteEntitiesRoute(
    deployment,
    options?.query as SettingsToRetrieve,
  )
  const response = createJsonResponse(result)
  const body = await response.json()

  return body
}

// Returns API server definitions
const handleApiServer = async <T extends Record<string, unknown> = Record<string, unknown>>(
  deployment: StackDeployment,
  mapRoute: RouteMapper,
  options?: T,
): Promise<unknown> => {
  const mappedPath = mapRoute('/api/console/api_server')
  const version = getElasticsearchVersion(deployment)

  // Add version as query parameter if available
  const url = version ? `${mappedPath}?version=${encodeURIComponent(version)}` : mappedPath

  const result = await makeHttpRequest(url, {
    method: 'GET',
    ...options,
  })
  return result.body
}

const handleDefaultGet = async <T extends Record<string, unknown> = Record<string, unknown>>(
  path: string,
  mapRoute: RouteMapper,
  options?: T,
): Promise<HttpResponse> => {
  const mappedPath = mapRoute(path)
  return await makeHttpRequest(mappedPath, {
    method: 'GET',
    ...options,
  })
}

export const handleGetRequest = async <T extends Record<string, unknown> = Record<string, unknown>>(
  path: string,
  deployment: StackDeployment,
  mapRoute: RouteMapper,
  options?: T,
): Promise<unknown> => {
  if (path === '/api/console/es_config') {
    return handleEsConfig(deployment)
  }

  if (path === '/api/console/autocomplete_entities') {
    return handleAutocompleteEntities(deployment, options)
  }

  if (path === '/api/console/api_server') {
    return handleApiServer(deployment, mapRoute, options)
  }

  return handleDefaultGet(path, mapRoute, options)
}
